import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Building, Package, Scale, Calculator, RefreshCw, User, Calendar } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./ProductUomConversions.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditProductUomConversions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userType, company } = useAuth();

  const [formData, setFormData] = useState({
    from_uom_id: "",
    to_uom_id: "",
    conversion_rate: "",
    status: "active"
  });
  const [conversion, setConversion] = useState(null);
  const [uoms, setUoms] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});

  const isSystemAdminUser = () => {
    return userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  };

  const fetchUoms = async (companyId) => {
    try {
      const response = await axios.get(`${url}/api/uom/dropdown`, {
        params: { company_id: companyId }
      });
      if (response.data.success) {
        setUoms(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load units of measure");
    }
  };

  const fetchConversion = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/puomc/${id}`);
      if (res.data.success) {
        const conv = res.data.data;
        setConversion(conv);
        setFormData({
          from_uom_id: conv.from_uom_id?._id || "",
          to_uom_id: conv.to_uom_id?._id || "",
          conversion_rate: conv.conversion_rate || "",
          status: conv.status || "active"
        });
        if (conv.company_id?._id) {
          await fetchUoms(conv.company_id._id);
        }
      } else {
        toast.error(res.data.message || "Failed to load conversion");
        navigate("/ViewProductUomConversions");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load conversion");
      navigate("/ViewProductUomConversions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversion();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (type === "number") {
      const numValue = parseFloat(value) || "";
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) =>
    setFocused({ ...focused, [field]: formData[field] && (typeof formData[field] === 'string' ? formData[field].length > 0 : true) });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.from_uom_id) newErrors.from_uom_id = "From UOM is required";
    if (!formData.to_uom_id) newErrors.to_uom_id = "To UOM is required";
    if (formData.from_uom_id && formData.to_uom_id && formData.from_uom_id === formData.to_uom_id)
      newErrors.to_uom_id = "From UOM and To UOM must be different";
    if (!formData.conversion_rate) newErrors.conversion_rate = "Conversion rate is required";
    else if (formData.conversion_rate <= 0) newErrors.conversion_rate = "Conversion rate must be greater than 0";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.put(`${url}/api/puomc/${id}`, formData);
      if (res.data.success) {
        toast.success("UOM conversion updated successfully");
        navigate("/ViewProductUomConversions");
      } else {
        toast.error(res.data.message || "Update failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate("/ViewProductUomConversions");

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="uom-form-container">
          <div className="loading-container">
            <RefreshCw className="animate-spin" size={32} />
            <p>Loading conversion details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!conversion) return null;

  return (
    <div className="form-wrapper">
      <div className="uom-form-container">
        <button className="close-btn" onClick={handleCancel} title="Close">
          <ArrowLeft size={20} />
        </button>

        <div className="form-header">
          <RefreshCw size={24} />
          <h2 className="form-title">Edit Product UOM Conversion</h2>
          <p className="form-subtitle">
            Update conversion details for {conversion.product_id?.product_name || "this product"}
          </p>
        </div>

        {/* Entity Information Banner */}
        <div className="entity-info" style={{
          background: '#f0f9ff',
          borderLeft: '4px solid #1890ff',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          fontSize: '0.85rem'
        }}>
          <div className="info-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Building size={14} color="#1890ff" />
            <span><strong>Company:</strong> {conversion.company_id?.company_name} ({conversion.company_id?.company_code})</span>
          </div>
          <div className="info-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Package size={14} color="#1890ff" />
            <span><strong>Product:</strong> {conversion.product_id?.product_name} ({conversion.product_id?.product_code})</span>
          </div>
          <div className="info-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <User size={14} color="#1890ff" />
            <span><strong>Created by:</strong> {conversion.created_by?.name || conversion.created_by?.Usercomp_sure_name || "System"}</span>
          </div>
          <div className="info-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Calendar size={14} color="#1890ff" />
            <span><strong>Created:</strong> {new Date(conversion.created_at).toLocaleDateString()}</span>
          </div>
          {conversion.updated_by && (
            <div className="info-item" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <User size={14} color="#1890ff" />
              <span><strong>Last updated by:</strong> {conversion.updated_by?.name || "System"} on {new Date(conversion.updated_at).toLocaleDateString()}</span>
            </div>
          )}
        </div>

        <form className="product-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* From UOM */}
            <div className={`form-field required ${focused.from_uom_id || formData.from_uom_id ? "filled" : ""} ${errors.from_uom_id ? 'error' : ''}`}>
              <div className="select-wrapper">
                <select
                  name="from_uom_id"
                  value={formData.from_uom_id}
                  onChange={handleChange}
                  onFocus={() => handleFocus("from_uom_id")}
                  onBlur={() => handleBlur("from_uom_id")}
                  required
                  disabled={uoms.length === 0}
                >
                  <option value="">Select From UOM</option>
                  {uoms.map((uom) => (
                    <option key={uom._id} value={uom._id}>
                      {uom.uom_name} ({uom.uom_type})
                    </option>
                  ))}
                </select>
                <Scale size={16} className="select-icon" />
              </div>
              <label>From Unit of Measure *</label>
              {errors.from_uom_id && <small className="field-error">{errors.from_uom_id}</small>}
            </div>

            {/* To UOM */}
            <div className={`form-field required ${focused.to_uom_id || formData.to_uom_id ? "filled" : ""} ${errors.to_uom_id ? 'error' : ''}`}>
              <div className="select-wrapper">
                <select
                  name="to_uom_id"
                  value={formData.to_uom_id}
                  onChange={handleChange}
                  onFocus={() => handleFocus("to_uom_id")}
                  onBlur={() => handleBlur("to_uom_id")}
                  required
                  disabled={uoms.length === 0}
                >
                  <option value="">Select To UOM</option>
                  {uoms.map((uom) => (
                    <option key={uom._id} value={uom._id}>
                      {uom.uom_name} ({uom.uom_type})
                    </option>
                  ))}
                </select>
                <Scale size={16} className="select-icon" />
              </div>
              <label>To Unit of Measure *</label>
              {errors.to_uom_id && <small className="field-error">{errors.to_uom_id}</small>}
            </div>

            {/* Conversion Rate */}
            <div className={`form-field required ${focused.conversion_rate || formData.conversion_rate ? "filled" : ""} ${errors.conversion_rate ? 'error' : ''}`}>
              <input
                type="number"
                name="conversion_rate"
                value={formData.conversion_rate}
                onChange={handleChange}
                onFocus={() => handleFocus("conversion_rate")}
                onBlur={() => handleBlur("conversion_rate")}
                placeholder="1.0"
                step="0.0001"
                min="0.0001"
                required
              />
              <label>Conversion Rate *</label>
              {errors.conversion_rate && <small className="field-error">{errors.conversion_rate}</small>}
              <small className="field-hint">Example: 1.5 means 1 From UOM = 1.5 To UOM</small>
            </div>

            {/* Conversion Preview */}
            {formData.conversion_rate && formData.conversion_rate > 0 && (
              <div className="conversion-preview">
                <div className="preview-item">
                  <Scale size={14} />
                  <span>1 From UOM = {formData.conversion_rate} To UOM</span>
                </div>
                <div className="preview-item">
                  <Calculator size={14} />
                  <span>Or 1 To UOM = {(1 / formData.conversion_rate).toFixed(4)} From UOM</span>
                </div>
              </div>
            )}

            {/* Status */}
            <div className="form-field">
              <div className="select-wrapper">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <RefreshCw size={16} className="select-icon" />
              </div>
              <label>Status</label>
              <small className="field-hint">
                {formData.status === 'active'
                  ? "Active conversions are used in transactions"
                  : "Inactive conversions are hidden from active lists"}
              </small>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={handleCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={16} />
                  Updating...
                </>
              ) : (
                "Update Conversion"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProductUomConversions;